import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { OnboardingLayout } from '../OnboardingLayout';
import { Card } from '../../../components/ui/Card';
import { Alert } from '../../../components/ui/Alert';
import { CopyField } from '../../../components/ui/CopyField';
import { API_DISPLAY_BASE_URL, ApiProblemError, completeOnboarding } from '../../../lib/api-client';
import { useApiKeys } from '../ApiKeysContext';
import { useAuth } from '../AuthContext';
import { useOnboardingState } from '../useOnboardingState';

function curlSnippet(apiKey: string): string {
  return [
    `curl -X POST '${API_DISPLAY_BASE_URL}/v1/quotes' \\`,
    `  -H 'Authorization: Bearer ${apiKey}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '{`,
    `    "from_postal_code": "01310930",`,
    `    "to_postal_code": "20040020",`,
    `    "packages": [{ "weight_g": 1000, "height_cm": 20, "width_cm": 20, "length_cm": 20 }]`,
    `  }'`,
  ].join('\n');
}

/**
 * Tela 5 — API key (/onboarding/api-key). Reveals the key(s) minted back in
 * Tela 2 (CnpjStep.tsx) — this is the first time the user SEES the raw
 * value, even though it was created earlier; the plaintext still only ever
 * appears in this one screen and is never retrievable again afterwards
 * (`ApiKeysService#create` returns `key` exactly once — see
 * apps/api/src/modules/auth/api-keys.service.ts). `trv_test_` is always
 * shown (it's the "default" key for the snippet below); `trv_live_` is
 * shown too only if Tela 3 actually connected a provider.
 */
export function ApiKeyStep() {
  const { keys, reset } = useApiKeys();
  const { state, update } = useOnboardingState();
  const { user, orgId } = useAuth();
  const navigate = useNavigate();
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  const hasLiveConnection = state.connectedProviders.length > 0;

  /**
   * TTP-266 incident fix: persists "onboarding completo" on the SERVER
   * (`orgs/{orgId}.onboarding_completed_at`) BEFORE navigating away — this
   * used to be a client-only `update({ step: 'completed' })`, which meant a
   * fresh browser/device/localStorage always looked like onboarding was
   * still pending, driving `ensureOneApiKey` (guards.tsx's
   * `EnsureApiKeysReady`) to revoke and recreate the onboarding API keys on
   * every such visit. Never navigates silently on failure — same
   * error+retry pattern as `guards.tsx`'s `EnsureApiKeysReady`.
   */
  async function handleFinish() {
    if (!user || !orgId) {
      // Should never happen at this step (the org was already created back
      // in CnpjStep.tsx) — nothing sensible to retry against here.
      return;
    }
    setFinishing(true);
    setFinishError(null);
    try {
      const idToken = await user.getIdToken();
      await completeOnboarding(idToken, orgId);
      update({ step: 'completed' });
      // The raw keys have now been shown once — drop them from memory too,
      // not just from the localStorage progress (which never held them).
      reset();
      navigate('/');
    } catch (err) {
      setFinishError(
        err instanceof ApiProblemError
          ? (err.problem.detail ?? err.problem.title)
          : 'Não foi possível concluir o onboarding agora.',
      );
    } finally {
      setFinishing(false);
    }
  }

  return (
    <OnboardingLayout
      step="api-key"
      title="Suas chaves de API"
      description="Guarde essas chaves com segurança — por segurança, elas não serão exibidas novamente depois desta tela."
    >
      <Alert variant="warning" title="Isso só aparece uma vez">
        Copie e guarde suas chaves agora. Depois desta tela, o Trivio nunca mais mostra o valor completo — só um
        prefixo para identificação.
      </Alert>

      {!keys.test && !keys.live ? (
        <Alert variant="error" title="Chaves indisponíveis">
          Não foi possível recuperar suas chaves de API nesta sessão — isso pode acontecer após recarregar a página.{' '}
          <Link to="/onboarding/providers" className="font-medium text-primary underline-offset-4 hover:underline">
            Volte para gerá-las novamente
          </Link>
          .
        </Alert>
      ) : null}

      {keys.test ? (
        <Card className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-foreground">Chave de sandbox</h2>
            <p className="text-sm text-muted-foreground">
              Para testar sua integração sem gerar cobranças ou etiquetas reais.
            </p>
          </div>
          <CopyField label="trv_test_…" value={keys.test.key} />
          <CodeSnippet code={curlSnippet(keys.test.key)} />
        </Card>
      ) : null}

      {hasLiveConnection && keys.live ? (
        <Card className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-foreground">Chave de produção</h2>
            <p className="text-sm text-muted-foreground">Para cotações e etiquetas reais junto aos provedores conectados.</p>
          </div>
          <CopyField label="trv_live_…" value={keys.live.key} />
          <CodeSnippet code={curlSnippet(keys.live.key)} />
        </Card>
      ) : null}

      {finishError ? (
        <Alert variant="error" title="Não foi possível concluir o onboarding">
          {finishError}
        </Alert>
      ) : null}

      <div className="flex justify-end border-t border-border pt-6">
        <Button type="button" variant="default" onClick={handleFinish} disabled={finishing}>
          {finishing ? 'Concluindo…' : finishError ? 'Tentar novamente' : 'Ir para o dashboard'}
        </Button>
      </div>
    </OnboardingLayout>
  );
}

function CodeSnippet({ code }: { code: string }) {
  return (
    <pre className="overflow-x-auto rounded-none border border-border bg-muted/30 p-4 font-mono text-xs leading-relaxed text-foreground">
      <code>{code}</code>
    </pre>
  );
}
